import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaCheckCircle,
  FaBars,
  FaClock,
  FaCommentDots,
  FaEye,
  FaEyeSlash,
  FaInfoCircle,
  FaLink,
  FaPlus,
  FaSave,
  FaShieldAlt,
  FaSyncAlt,
  FaTrash,
  FaUserCheck,
} from 'react-icons/fa';
import { useUser } from '../../contexts/userContext';
import { useColumns } from '../../contexts/columnsContext';
import { GlobalContext } from '../../contexts/GlobalContext';
import api from '../../services/api';
import { INTERFACE_LAYOUT } from '../../utils/interfaceLayout';
import Avatar from '../Avatar';
import Header from '../Header';
import ImportExcelSuiteFlow from '../ImportExcelSuiteFlow';
import WhatsappZapiFloatingButton from '../WhatsappZapi/WhatsappZapiFloatingButton';
import NewSidebar from '../pages/HomeNewPage/components/NewSidebar';
import '../pages/HomeNewPage/style.css';
import './WhatsappMeta.css';
import { UTILITY_BAD_EXAMPLE, UTILITY_GOOD_EXAMPLE, utilityTemplateWarnings } from './templateCategoryHints';

const FACEBOOK_ORIGINS = new Set(['https://www.facebook.com', 'https://web.facebook.com']);
const TEMPLATE_DEFAULT = {
  name: 'syncs_confirmacao_atendimento',
  language: 'pt_BR',
  category: 'UTILITY',
  text: 'Olá! Seu atendimento foi registrado com sucesso. Se precisar, responda a esta mensagem.',
};

const TEMPLATE_STATUS_LABELS = {
  APPROVED: 'Aprovado',
  PENDING: 'Em análise',
  REJECTED: 'Rejeitado',
  PAUSED: 'Pausado',
  DISABLED: 'Desativado',
  REMOVED: 'Removido da conta',
};

function templateStatusLabel(status) {
  const normalized = String(status || '').toUpperCase();
  return TEMPLATE_STATUS_LABELS[normalized] || 'Aguardando atualização';
}

function categoryLabel(category) {
  if (category === 'MARKETING') return 'Marketing';
  if (category === 'AUTHENTICATION') return 'Autenticação';
  return 'Utilidade';
}

function hasTemplateRejectionReason(reason) {
  const normalized = String(reason || '').trim().toUpperCase();
  return Boolean(normalized) && !['NONE', 'NULL'].includes(normalized);
}

function channelQualityLabel(quality) {
  const normalized = String(quality || '').toUpperCase();
  if (normalized === 'GREEN') return 'Alta';
  if (normalized === 'YELLOW') return 'Média — acompanhe com atenção';
  if (normalized === 'RED') return 'Baixa - marketing bloqueado pelo CRM';
  return 'Ainda não classificada';
}

function channelRegistrationLabel(status) {
  const normalized = String(status || '').trim().toLowerCase();
  if (normalized === 'registered') return 'Registrado';
  if (normalized === 'verified') return 'Verificado';
  if (normalized === 'pending_registration') return 'Registro pendente';
  if (normalized === 'pending') return 'Pendente';
  if (normalized === 'not_verified') return 'Não verificado';
  return 'Aguardando atualização';
}

function formatLimitUpdate(value) {
  if (!value) return '';
  return new Date(value).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

function requestErrorMessage(error, fallback) {
  const message = error?.response?.data?.message || error?.message;
  if (Array.isArray(message)) return message.join(' ');
  return String(message || fallback);
}

function parseEmbeddedEvent(event) {
  if (!FACEBOOK_ORIGINS.has(event.origin)) return null;
  try {
    const payload = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
    return payload?.type === 'WA_EMBEDDED_SIGNUP' ? payload : null;
  } catch (error) {
    return null;
  }
}

function loadFacebookSdk(appId, version, locale) {
  return new Promise((resolve, reject) => {
    const initialize = () => {
      if (!window.FB) return;
      window.FB.init({ appId, autoLogAppEvents: true, xfbml: false, version });
      resolve(window.FB);
    };
    if (window.FB) {
      initialize();
      return;
    }
    const previousInit = window.fbAsyncInit;
    window.fbAsyncInit = () => {
      previousInit?.();
      initialize();
    };
    let script = document.getElementById('facebook-jssdk');
    if (!script) {
      script = document.createElement('script');
      script.id = 'facebook-jssdk';
      script.async = true;
      script.defer = true;
      script.crossOrigin = 'anonymous';
      script.src = `https://connect.facebook.net/${locale || 'pt_BR'}/sdk.js`;
      script.onerror = () => reject(new Error('Não foi possível carregar o SDK oficial da Meta.'));
      document.body.appendChild(script);
    }
    setTimeout(() => reject(new Error('Tempo esgotado ao carregar o SDK oficial da Meta.')), 15000);
  });
}

function WhatsappMetaSettings() {
  const navigate = useNavigate();
  const { logout } = useContext(GlobalContext);
  const {
    user,
    listAllUsers,
    getAccessLevel,
    clearUserContext,
    openCloseModalAvatar,
    openCloseImportExcelSuiteFlow,
    updateUser,
  } = useUser();
  const { columns = [], boards = [] } = useColumns() || {};
  const isNewLayout = user?.interface_layout === INTERFACE_LAYOUT.new;
  const [interfaceTheme, setInterfaceTheme] = useState(
    () => user?.home_new_theme === 'dark' ? 'dark' : 'light',
  );
  const [interfaceThemeSaving, setInterfaceThemeSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [channels, setChannels] = useState([]);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [scope, setScope] = useState('user');
  const [ownerUserId, setOwnerUserId] = useState('');
  const [mode, setMode] = useState('cloud_api');
  const [pin, setPin] = useState('');
  const [showPin, setShowPin] = useState(false);
  const [registrationPins, setRegistrationPins] = useState({});
  const [visibleRegistrationPins, setVisibleRegistrationPins] = useState({});
  const [permissionDrafts, setPermissionDrafts] = useState({});
  const [cardDrafts, setCardDrafts] = useState({});
  const [templateEditors, setTemplateEditors] = useState({});
  const [templateDrafts, setTemplateDrafts] = useState({});
  const [templateResults, setTemplateResults] = useState({});
  const [channelTemplates, setChannelTemplates] = useState({});
  const [channelMessages, setChannelMessages] = useState({});
  const [mobileNavigationOpen, setMobileNavigationOpen] = useState(false);
  const pendingSignupRef = useRef(null);

  useEffect(() => {
    setInterfaceTheme(user?.home_new_theme === 'dark' ? 'dark' : 'light');
  }, [user?.home_new_theme, user?.id]);

  const toggleInterfaceTheme = async () => {
    if (!user?.id || interfaceThemeSaving) return;
    const previousTheme = interfaceTheme;
    const nextTheme = interfaceTheme === 'dark' ? 'light' : 'dark';

    setInterfaceThemeSaving(true);
    setInterfaceTheme(nextTheme);
    updateUser({ home_new_theme: nextTheme });
    try {
      await api.put(`/users/${user.id}`, { home_new_theme: nextTheme });
    } catch {
      setInterfaceTheme(previousTheme);
      updateUser({ home_new_theme: previousTheme });
      setMessage('Não foi possível salvar a preferência de tema.');
    } finally {
      setInterfaceThemeSaving(false);
    }
  };

  const handleLogout = () => {
    clearUserContext();
    logout();
    navigate('/');
  };

  const load = useCallback(async () => {
    try {
      const configResponse = await api.get('/whatsapp-meta/config');
      setConfig(configResponse.data || {});
      if (configResponse.data?.enabled) {
        const channelsResponse = await api.get('/whatsapp-meta/channels');
        const items = channelsResponse.data || [];
        setChannels(items);
        const drafts = {};
        items.forEach((channel) => { drafts[channel.id] = channel.users || []; });
        setPermissionDrafts(drafts);
        setCardDrafts({});
        const templateEntries = await Promise.all(items.map(async (channel) => {
          try {
            const response = await api.get(`/whatsapp-meta/channels/${channel.id}/templates`);
            return { channelId: channel.id, templates: response.data || [], error: '' };
          } catch (error) {
            return {
              channelId: channel.id,
              templates: null,
              error: requestErrorMessage(error, 'Não foi possível carregar os modelos deste número.'),
            };
          }
        }));
        setChannelTemplates((current) => {
          const next = { ...current };
          templateEntries.forEach((entry) => {
            if (entry.templates !== null) next[entry.channelId] = entry.templates;
          });
          return next;
        });
        setChannelMessages((current) => {
          const next = { ...current };
          templateEntries.forEach((entry) => {
            if (entry.error) next[entry.channelId] = { type: 'error', text: entry.error };
          });
          return next;
        });
      }
    } catch (error) {
      setConfig({ enabled: false });
      setMessage(error.response?.data?.message || 'O canal oficial ainda não está liberado para este usuário.');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const completeWhenReady = useCallback(async () => {
    const pending = pendingSignupRef.current;
    if (!pending || pending.completing || !pending.code || !pending.embedded) return;
    pending.completing = true;
    setBusy(true);
    try {
      await api.post('/whatsapp-meta/onboarding/complete', {
        state: pending.session.state,
        code: pending.code,
        business_id: pending.embedded.business_id || pending.embedded.businessId,
        waba_id: pending.embedded.waba_id || pending.embedded.wabaId,
        phone_number_id: pending.embedded.phone_number_id || pending.embedded.phoneNumberId,
        pin: pending.session.sdk.feature_type ? undefined : (pin || undefined),
      });
      pendingSignupRef.current = null;
      setPin('');
      setShowPin(false);
      setMessage('Número conectado oficialmente pela Meta.');
      await load();
    } catch (error) {
      pendingSignupRef.current = null;
      setMessage(error.response?.data?.message || error.message || 'Falha ao concluir a conexão com a Meta.');
    } finally {
      setPin('');
      setShowPin(false);
      setBusy(false);
    }
  }, [load, pin]);

  useEffect(() => {
    const listener = (event) => {
      const payload = parseEmbeddedEvent(event);
      if (!payload || !pendingSignupRef.current) return;
      if (payload.event === 'FINISH' || payload.event === 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING') {
        pendingSignupRef.current.embedded = payload.data || {};
        void completeWhenReady();
      } else if (payload.event === 'CANCEL' || payload.event === 'ERROR') {
        const pending = pendingSignupRef.current;
        pendingSignupRef.current = null;
        void api.post('/whatsapp-meta/onboarding/cancel', {
          state: pending.session.state,
          reason: payload.data?.error_message || payload.event,
        }).catch(() => undefined);
        setBusy(false);
        setPin('');
        setShowPin(false);
        setMessage(payload.data?.error_message || 'Conexão cancelada antes de concluir.');
      }
    };
    window.addEventListener('message', listener);
    return () => window.removeEventListener('message', listener);
  }, [completeWhenReady]);

  const connect = async () => {
    if (!config?.onboarding_enabled || busy) return;
    if (!window.confirm(
      'Confirme antes de abrir a Meta:\n\n'
      + '• Você está autorizado a administrar o portfólio da sua própria empresa.\n'
      + '• O número e a conta WhatsApp Business escolhidos pertencem à sua empresa e não estão ativos em outra tenant do SyncsCRM.\n'
      + '• Administradores da mesma tenant poderão administrar este canal, mesmo no modo Individual.\n'
      + (mode === 'coexistence'
        ? '• Você escolheu coexistência com o aplicativo WhatsApp Business.\n\n'
        : '• Esta opção usa somente Cloud API; ela não mantém o número funcionando ao mesmo tempo no aplicativo WhatsApp Business.\n\n')
      + 'Deseja continuar?',
    )) return;
    setBusy(true);
    setMessage('');
    try {
      const response = await api.post('/whatsapp-meta/onboarding/session', {
        scope,
        owner_user_id: ownerUserId || user?.id,
        onboarding_mode: mode,
      });
      const session = response.data;
      pendingSignupRef.current = { session, code: null, embedded: null, completing: false };
      const FB = await loadFacebookSdk(
        session.sdk.app_id,
        session.sdk.graph_version,
        session.sdk.locale,
      );
      const extras = { setup: {} };
      if (session.sdk.feature_type) {
        extras.featureType = session.sdk.feature_type;
        extras.sessionInfoVersion = '3';
      }
      FB.login((loginResponse) => {
        const code = loginResponse?.authResponse?.code;
        if (!code || !pendingSignupRef.current) {
          const pending = pendingSignupRef.current;
          pendingSignupRef.current = null;
          if (pending) void api.post('/whatsapp-meta/onboarding/cancel', { state: pending.session.state, reason: 'Login Meta não concluído' }).catch(() => undefined);
          setBusy(false);
          setPin('');
          setShowPin(false);
          setMessage('Login da Meta não foi concluído.');
          return;
        }
        pendingSignupRef.current.code = code;
        void completeWhenReady();
      }, {
        config_id: session.sdk.configuration_id,
        response_type: 'code',
        override_default_response_type: true,
        extras,
      });
    } catch (error) {
      pendingSignupRef.current = null;
      setBusy(false);
      setPin('');
      setShowPin(false);
      setMessage(error.response?.data?.message || error.message || 'Não foi possível iniciar o cadastro pela Meta.');
    }
  };

  const runAction = async (action, successMessage) => {
    setBusy(true);
    setMessage('');
    try {
      await action();
      setMessage(successMessage);
      await load();
    } catch (error) {
      setMessage(error.response?.data?.message || error.message || 'Operação não concluída.');
    } finally {
      setBusy(false);
    }
  };

  const updateChannel = (channel, changes) => runAction(
    () => api.put(`/whatsapp-meta/channels/${channel.id}`, changes),
    'Configuração do número atualizada.',
  );

  const togglePermission = (channelId, targetUserId) => {
    setPermissionDrafts((current) => {
      const users = current[channelId] || [];
      const exists = users.some((entry) => Number(entry.user_id) === Number(targetUserId));
      return {
        ...current,
        [channelId]: exists
          ? users.filter((entry) => Number(entry.user_id) !== Number(targetUserId))
          : [...users, { user_id: Number(targetUserId), can_view: true, can_send: true, can_configure: false }],
      };
    });
  };

  const cardDraft = (channel) => {
    if (cardDrafts[channel.id]) return cardDrafts[channel.id];
    const column = columns.find((item) => Number(item.id) === Number(channel.default_column_id));
    return {
      board_id: column?.board_id ? String(column.board_id) : '',
      column_id: channel.default_column_id ? String(channel.default_column_id) : '',
      origem: channel.default_origem || 'WhatsApp Meta',
      auto: channel.auto_create_card === true,
    };
  };

  const updateCardDraft = (channel, changes) => {
    setCardDrafts((current) => ({ ...current, [channel.id]: { ...cardDraft(channel), ...changes } }));
  };

  const saveCardSettings = (channel) => {
    const draft = cardDraft(channel);
    if (draft.auto && !draft.column_id) {
      setChannelMessage(channel.id, 'error', 'Selecione a coluna onde o card será criado automaticamente.');
      return;
    }
    void runAction(
      () => api.put(`/whatsapp-meta/channels/${channel.id}`, {
        default_column_id: draft.column_id ? Number(draft.column_id) : null,
        default_origem: draft.origem.trim(),
        auto_create_card: draft.auto,
      }),
      'Criação automática de card atualizada.',
    );
  };

  const templateDraft = (channelId) => templateDrafts[channelId] || TEMPLATE_DEFAULT;

  const updateTemplateDraft = (channelId, changes) => {
    setTemplateDrafts((current) => ({
      ...current,
      [channelId]: { ...(current[channelId] || TEMPLATE_DEFAULT), ...changes },
    }));
  };

  const setChannelMessage = (channelId, type, text) => {
    setChannelMessages((current) => ({ ...current, [channelId]: { type, text } }));
  };

  const syncChannelTemplates = async (channel) => {
    setBusy(true);
    setChannelMessage(channel.id, 'loading', 'Consultando os modelos na Meta...');
    try {
      const response = await api.post(`/whatsapp-meta/channels/${channel.id}/templates/sync`);
      try {
        const templatesResponse = await api.get(`/whatsapp-meta/channels/${channel.id}/templates`);
        const templates = templatesResponse.data || [];
        setChannelTemplates((current) => ({ ...current, [channel.id]: templates }));
        const total = Number(response.data?.total ?? templates.length);
        setChannelMessage(
          channel.id,
          total > 0 ? 'success' : 'warning',
          total > 0
            ? `${total} modelo(s) recebido(s) da Meta e atualizado(s) no CRM.`
            : 'A Meta retornou zero modelos. Modelos recém-criados que ainda estejam em análise serão preservados no CRM por 48 horas.',
        );
      } catch (error) {
        setChannelMessage(
          channel.id,
          'warning',
          `A sincronização foi concluída, mas a lista local não pôde ser recarregada. ${requestErrorMessage(error, '')}`.trim(),
        );
      }
    } catch (error) {
      setChannelMessage(channel.id, 'error', requestErrorMessage(error, 'Não foi possível atualizar os modelos da Meta.'));
    } finally {
      setBusy(false);
    }
  };

  const createTemplate = async (channel) => {
    const draft = templateDraft(channel.id);
    const categoryAdvice = draft.category === 'MARKETING'
      ? 'Marketing: ofertas, promoções e divulgação. O envio dessa categoria pode estar desabilitado no CRM mesmo após a aprovação do modelo.'
      : 'Utilidade: confirmação ou atualização específica de um pedido, serviço ou ação do cliente. Não use para ofertas, prospecção ou publicidade.';
    const confirmed = window.confirm(
      `${categoryAdvice}\n\nEste modelo será criado na conta conectada e enviado para análise da Meta. A Meta pode alterar a categoria, recusar ou pausar o modelo; ele só poderá ser usado quando estiver aprovado. Confirma que o conteúdo corresponde à finalidade escolhida?`,
    );
    if (!confirmed) return;
    setBusy(true);
    setMessage('');
    setChannelMessage(channel.id, 'loading', 'Criando o modelo na Meta e aguardando a confirmação...');
    try {
      const response = await api.post(`/whatsapp-meta/channels/${channel.id}/templates`, draft);
      const created = response.data;
      setTemplateResults((current) => ({ ...current, [channel.id]: created }));
      setChannelTemplates((current) => {
        const existing = current[channel.id] || [];
        const matchesCreated = (template) => (
          Number(template.id) === Number(created.id)
          || (template.name === created.name && template.language === created.language)
        );
        return { ...current, [channel.id]: [created, ...existing.filter((template) => !matchesCreated(template))] };
      });
      setChannelMessage(
        channel.id,
        'success',
        `Modelo ${created.name} criado na Meta. Status atual: ${templateStatusLabel(created.status)}.`
          + (draft.category === 'UTILITY'
            ? ' A Meta confirma a categoria em alguns minutos; se ela trocar para Marketing, o modelo aparecerá marcado na lista acima.'
            : ''),
      );
      try {
        const templatesResponse = await api.get(`/whatsapp-meta/channels/${channel.id}/templates`);
        setChannelTemplates((current) => ({ ...current, [channel.id]: templatesResponse.data || [] }));
      } catch (error) {
        setChannelMessage(
          channel.id,
          'warning',
          `O modelo ${created.name} foi criado, mas a lista não pôde ser recarregada agora. Não crie novamente; use Atualizar modelos mais tarde.`,
        );
      }
    } catch (error) {
      setChannelMessage(channel.id, 'error', requestErrorMessage(error, 'Não foi possível criar o modelo na Meta.'));
    } finally {
      setBusy(false);
    }
  };

  const content = (
    <main className="meta-whatsapp-settings-page">
        <section className="meta-whatsapp-header">
          <div className="meta-whatsapp-header-title">
            <img src="/syncs-logo-meta-v1.png" alt="" aria-hidden="true" />
            <div>
              <span className="meta-whatsapp-eyebrow">Syncs Messenger</span>
              <h1>Canais oficiais</h1>
              <p>Conecte e administre o atendimento oficial pelo WhatsApp Business.</p>
            </div>
          </div>
          <span className={config?.enabled ? 'meta-status-ok' : 'meta-status-off'}>
            {config?.enabled ? 'Disponível' : 'Indisponível com segurança'}
          </span>
        </section>

        <details className="meta-whatsapp-customer-guide">
          <summary>Passo a passo: conectar e usar o WhatsApp Oficial</summary>
          <ol>
            <li>Entre no SyncsCRM da sua empresa e abra <strong>WhatsApp Oficial</strong>. Use uma conta com autorização para administrar o portfólio empresarial da Meta.</li>
            <li>Confira se o número é da sua empresa e pode receber o código de verificação. Se ele já funciona no aplicativo WhatsApp Business e você precisa continuar usando o aplicativo, só avance se a opção <strong>Coexistir com WhatsApp Business</strong> estiver disponível e selecionada.</li>
            <li>Em <strong>Conectar um número</strong>, escolha o responsável. Comece com <strong>Individual</strong> para limitar usuários comuns; administradores da mesma tenant continuam com acesso. O modo compartilhado pode abrir a visualização para toda a tenant. Clique em <strong>Autorizar e conectar na Meta</strong>; na janela oficial da Meta, selecione o portfólio, a conta WhatsApp Business e o número corretos. Conclua a autorização e a verificação solicitadas pela Meta.</li>
            <li>Ao voltar, confira o número exibido. Se aparecer <strong>Registro pendente</strong>, informe o PIN de seis dígitos e clique em <strong>Registrar número</strong>. Depois use <strong>Verificar conexão</strong> e faça um teste de recebimento e resposta com um contato autorizado.</li>
            <li>Se outras pessoas da sua equipe forem atender, selecione somente os usuários necessários em <strong>Usuários autorizados a visualizar e enviar</strong>, clique em <strong>Salvar permissões</strong> e mude <strong>Quem pode enviar</strong> para <strong>Usuários autorizados</strong>. Não é necessário pedir liberação individual ao suporte.</li>
            <li>Para conectar outro número, repita o processo. Cada número aparece como canal separado; antes de responder ou enviar um modelo, confira o número remetente selecionado na conversa.</li>
            <li>Para iniciar ou retomar contato fora da janela de 24 horas, use somente um modelo aprovado e registre a origem e a evidência verdadeira da autorização do destinatário. <strong>Utilidade</strong> é para avisos específicos de transação ou serviço; ofertas e divulgação são <strong>Marketing</strong>. A Meta pode cobrar mensagens e rever a categoria ou aprovação do modelo.</li>
          </ol>
          <p>Não use o portfólio ou o número de outra empresa. Se a mesma conta WhatsApp Business estiver ativa em outra tenant do SyncsCRM, desconecte primeiro os canais de teste dessa conta na tenant anterior; o histórico continua nela. Se o mesmo número já estiver cadastrado na tenant anterior, interrompa o cadastro e solicite uma migração segura: desconectar não transfere o histórico nem libera o vínculo do número.</p>
          <a href="https://whatsappbusiness.com/pt-br/policy/" target="_blank" rel="noopener noreferrer">Leia a Política de Mensagens do WhatsApp Business</a>
        </details>

        <section className="meta-whatsapp-safety-box">
          <div className="meta-whatsapp-section-title">
            <span className="meta-whatsapp-section-icon"><FaShieldAlt /></span>
            <div>
              <strong>Conexão oficial, com proteção de envio</strong>
              <span>A integração usa a Cloud API da Plataforma do WhatsApp Business, hospedada pela Meta, e fica disponível somente para usuários autorizados.</span>
            </div>
          </div>
          <div className="meta-whatsapp-flow-guide">
            <article>
              <FaCommentDots />
              <div>
                <strong>Quando o cliente escreve</strong>
                <span>A equipe pode responder normalmente durante a janela de atendimento de 24 horas.</span>
              </div>
            </article>
            <article>
              <FaClock />
              <div>
                <strong>Para iniciar ou retomar</strong>
                <span>Use um modelo aprovado e registre o consentimento do cliente antes do envio.</span>
              </div>
            </article>
            <article>
              <FaUserCheck />
              <div>
                <strong>Controle pelo CRM</strong>
                <span>Permissões, janela de atendimento e qualidade do canal são verificadas antes do envio.</span>
              </div>
            </article>
          </div>
          <p className="meta-whatsapp-definition">
            <FaInfoCircle /> Um modelo é uma mensagem previamente aprovada para iniciar ou retomar uma conversa. Ele não é uma resposta automática e só é enviado quando um usuário autorizado o seleciona.
          </p>
          <p className="meta-whatsapp-independence-note">
            SyncsCRM é uma plataforma independente. WhatsApp e Meta são marcas de seus respectivos proprietários; esta integração não representa patrocínio ou endosso e depende da autorização da empresa na Meta.
          </p>
        </section>

        {message && <div className="meta-whatsapp-message">{message}</div>}

        {config?.enabled && (
          <section className="meta-whatsapp-connect-panel">
            <div className="meta-whatsapp-panel-heading">
              <div>
                <span className="meta-whatsapp-step">Novo canal</span>
                <h2>Conectar um número</h2>
                <p>A empresa vincula a própria conta comercial pelo fluxo de autorização da Meta e mantém o controle de números, limites e cobranças.</p>
              </div>
            </div>
            <div className="meta-whatsapp-form-grid">
              <label>
                Responsável no CRM
                <select value={ownerUserId} onChange={(event) => setOwnerUserId(event.target.value)}>
                  <option value="">Usuário atual</option>
                  {listAllUsers.map((item) => <option key={item.id} value={item.id}>{item.username}</option>)}
                </select>
              </label>
              <label>
                Uso do número
                <select value={scope} onChange={(event) => {
                  const nextScope = event.target.value;
                  if (nextScope === 'company' && !window.confirm('Compartilhar com a empresa permite que todos os usuários desta tenant vejam o canal, inclusive usuários externos vinculados a ela. Confirma?')) return;
                  setScope(nextScope);
                }}>
                  <option value="user">Individual</option>
                  <option value="company">Compartilhado pela empresa</option>
                </select>
              </label>
              <label>
                Modalidade
                <select value={mode} onChange={(event) => {
                  setMode(event.target.value);
                  setPin('');
                }}>
                  <option value="cloud_api">Somente Cloud API</option>
                  {config.coexistence_available && <option value="coexistence">Coexistir com WhatsApp Business</option>}
                </select>
              </label>
              {mode === 'cloud_api' && (
                <label>
                  PIN de 2 etapas (se solicitado)
                  <span className="meta-whatsapp-secret-field">
                    <input type={showPin ? 'text' : 'password'} value={pin} onChange={(event) => setPin(event.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="6 dígitos" inputMode="numeric" autoComplete="new-password" />
                    <button type="button" onClick={() => setShowPin((current) => !current)} aria-label={showPin ? 'Ocultar PIN' : 'Mostrar PIN'} title={showPin ? 'Ocultar PIN' : 'Mostrar PIN'}>
                      {showPin ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </span>
                </label>
              )}
            </div>
            <button type="button" onClick={connect} disabled={busy || !config.onboarding_enabled}>
              <FaLink /> {busy ? 'Aguarde...' : 'Autorizar e conectar na Meta'}
            </button>
            <small className="meta-whatsapp-authorization-note">
              A conexão só é concluída quando um administrador autorizado finaliza o fluxo apresentado pela Meta.
            </small>
            {!config.onboarding_enabled && <p>O cadastro será liberado após a configuração e a aprovação do aplicativo na Meta.</p>}
          </section>
        )}

        <section className="meta-whatsapp-channel-list">
          <div className="meta-whatsapp-list-heading">
            <div>
              <span className="meta-whatsapp-eyebrow">Atendimento conectado</span>
              <h2>Números oficiais</h2>
            </div>
            <span>{channels.length} conectado(s)</span>
          </div>
          {channels.length === 0 && <p>Nenhum número oficial conectado.</p>}
          {channels.map((channel) => {
            const draft = templateDraft(channel.id);
            const templateResult = templateResults[channel.id];
            const channelMessage = channelMessages[channel.id];
            const templates = channelTemplates[channel.id] || [];
            const templateSyncTimes = templates
              .map((template) => new Date(template.last_synced_at).getTime())
              .filter(Number.isFinite);
            const latestTemplateSyncAt = templateSyncTimes.length
              ? new Date(Math.max(...templateSyncTimes))
              : null;
            return (
            <article key={channel.id} className="meta-whatsapp-channel-card">
              <div className="meta-whatsapp-channel-summary">
                <div>
                   <strong>{channel.verified_name || channel.name}</strong>
                   <span>{channel.phone_display || channel.phone_normalized}</span>
                   <span>Responsável: {channel.owner_username || channel.owner_user_id}</span>
                   <span>{channel.onboarding_mode === 'coexistence' ? 'Coexistência com o aplicativo empresarial' : 'Canal oficial exclusivo'}</span>
                </div>
                <div>
                  <span className={channel.is_active && !channel.token_expired ? 'meta-status-ok' : 'meta-status-off'}>
                    {channel.token_expired ? 'Credencial expirada' : channel.is_active ? 'Ativo' : 'Desconectado'}
                  </span>
                   <span>Qualidade: {channelQualityLabel(channel.quality_rating)}</span>
                   <span>Registro: {channelRegistrationLabel(channel.registration_status)}</span>
                   {channel.messaging_limit_value && (
                     <span title="Informação recebida da Meta; não é uma garantia de disponibilidade futura.">
                       Capacidade informada: {channel.messaging_limit_value}
                       {channel.messaging_limit_scope === 'business_portfolio' ? ' no portfólio' : ' no número'}
                       {channel.messaging_limit_updated_at ? ` · ${formatLimitUpdate(channel.messaging_limit_updated_at)}` : ''}
                     </span>
                   )}
                   {channel.last_error && <span className="meta-status-warning">Atenção: {channel.last_error}</span>}
                   {Number(channel.processing_alert_count || 0) > 0 && (
                     <span className="meta-status-warning">
                       Atenção: {channel.processing_alert_count} evento(s) precisam de recuperação ou análise.
                     </span>
                   )}
                   {Number(channel.unknown_send_count || 0) > 0 && (
                     <span className="meta-status-warning">
                       Atenção: {channel.unknown_send_count} envio(s) aguardam confirmação. Não reenvie antes da reconciliação.
                     </span>
                   )}
                </div>
              </div>

              <div className="meta-whatsapp-channel-controls">
                <label>Quem pode acessar
                  <select value={channel.scope} onChange={(event) => {
                    const nextScope = event.target.value;
                    if (nextScope === 'company' && !window.confirm('Compartilhar com a empresa permite que todos os usuários desta tenant vejam o canal. Confirma?')) return;
                    updateChannel(channel, { scope: nextScope });
                  }} disabled={busy}>
                    <option value="user">Individual</option>
                    <option value="company">Empresa</option>
                  </select>
                </label>
                <label>Quem pode enviar
                  <select value={channel.send_policy} onChange={(event) => {
                    const nextPolicy = event.target.value;
                    if (nextPolicy === 'company' && !window.confirm('Toda empresa permite enviar por este número a todos os usuários desta tenant. Use Usuários autorizados para limitar a equipe. Confirma?')) return;
                    updateChannel(channel, { send_policy: nextPolicy });
                  }} disabled={busy}>
                    <option value="owner_only">Somente responsável</option>
                    <option value="allowed_users">Usuários autorizados</option>
                    {channel.scope === 'company' && <option value="company">Toda empresa</option>}
                  </select>
                </label>
                <button type="button" disabled={busy} onClick={() => runAction(() => api.post(`/whatsapp-meta/channels/${channel.id}/health`), 'Conexão verificada na Meta.')} title="Consulta o estado atual do número"><FaCheckCircle /> Verificar conexão</button>
                <button type="button" disabled={busy} onClick={() => void syncChannelTemplates(channel)} title="Atualiza a lista de modelos desta conta"><FaSyncAlt /> Atualizar modelos</button>
                <button type="button" disabled={busy} onClick={() => runAction(() => api.post(`/whatsapp-meta/channels/${channel.id}/webhooks/reprocess`), 'Mensagens com processamento pendente foram reenfileiradas.')} title="Tenta processar novamente mensagens que ficaram pendentes"><FaSyncAlt /> Recuperar mensagens</button>
                {channel.onboarding_mode === 'coexistence' && (
                  <button type="button" disabled={busy} onClick={() => runAction(() => api.post(`/whatsapp-meta/channels/${channel.id}/coexistence-sync`), 'Sincronização de contatos e histórico solicitada.')}><FaSyncAlt /> Atualizar aplicativo empresarial</button>
                )}
              </div>

              <div className="meta-whatsapp-permissions">
                <strong>Usuários autorizados a visualizar e enviar</strong>
                <div>
                  {listAllUsers.map((item) => {
                    const checked = (permissionDrafts[channel.id] || []).some((entry) => Number(entry.user_id) === Number(item.id));
                    return <label key={item.id}><input type="checkbox" checked={checked} onChange={() => togglePermission(channel.id, item.id)} /> {item.username}</label>;
                  })}
                </div>
                <button type="button" disabled={busy} onClick={() => runAction(
                  () => api.put(`/whatsapp-meta/channels/${channel.id}/users`, { users: permissionDrafts[channel.id] || [] }),
                  'Permissões do número atualizadas.',
                )}><FaSave /> Salvar permissões</button>
              </div>

              {(() => {
                const draft = cardDraft(channel);
                const boardColumns = columns.filter((column) => (
                  column.active !== false
                  && (!draft.board_id || String(column.board_id) === draft.board_id)
                ));
                return (
                  <div className="meta-whatsapp-auto-card">
                    <div className="meta-whatsapp-auto-card-heading">
                      <strong>Criação automática de card</strong>
                      <span>Quando um contato sem card enviar a primeira mensagem para este número, o CRM cria o card no board e na coluna escolhidos, com o responsável do número e a origem padrão.</span>
                    </div>
                    <label className="meta-whatsapp-auto-card-toggle">
                      <input
                        type="checkbox"
                        checked={draft.auto}
                        onChange={(event) => updateCardDraft(channel, { auto: event.target.checked })}
                      /> Criar card automaticamente
                    </label>
                    <div className="meta-whatsapp-auto-card-grid">
                      <label>Board
                        <select
                          value={draft.board_id}
                          onChange={(event) => updateCardDraft(channel, { board_id: event.target.value, column_id: '' })}
                        >
                          <option value="">Todos os boards</option>
                          {boards.map((board) => <option key={board.id} value={String(board.id)}>{board.name}</option>)}
                        </select>
                      </label>
                      <label>Coluna
                        <select
                          value={draft.column_id}
                          onChange={(event) => {
                            const column = columns.find((item) => String(item.id) === event.target.value);
                            updateCardDraft(channel, {
                              column_id: event.target.value,
                              board_id: draft.board_id || (column?.board_id ? String(column.board_id) : ''),
                            });
                          }}
                        >
                          <option value="">Selecione a coluna</option>
                          {boardColumns.map((column) => <option key={column.id} value={String(column.id)}>{column.name}</option>)}
                        </select>
                      </label>
                      <label>Origem padrão
                        <input
                          value={draft.origem}
                          maxLength={120}
                          onChange={(event) => updateCardDraft(channel, { origem: event.target.value })}
                          placeholder="WhatsApp Meta"
                        />
                      </label>
                      <button type="button" disabled={busy} onClick={() => saveCardSettings(channel)}>
                        <FaSave /> Salvar criação de card
                      </button>
                    </div>
                  </div>
                );
              })()}

              <div className="meta-whatsapp-template-review">
                {channelMessage && (
                  <div
                    className={`meta-whatsapp-channel-message meta-whatsapp-channel-message--${channelMessage.type}`}
                    role={channelMessage.type === 'error' ? 'alert' : 'status'}
                    aria-live="polite"
                  >
                    {channelMessage.text}
                  </div>
                )}
                <div className="meta-whatsapp-template-list-heading">
                  <div>
                    <strong>Modelos de mensagem</strong>
                    <span>Mensagens aprovadas para iniciar um atendimento ou retomar uma conversa depois de 24 horas.</span>
                  </div>
                  <div className="meta-whatsapp-template-sync-summary">
                    <span>{templates.length} modelo(s)</span>
                    <small>
                      {latestTemplateSyncAt
                        ? `Última atualização: ${latestTemplateSyncAt.toLocaleString('pt-BR')}`
                        : 'Nenhum modelo sincronizado'}
                    </small>
                  </div>
                </div>
                {templates.length === 0 ? (
                  <p className="meta-whatsapp-template-empty">Nenhum modelo encontrado neste número. Atualize a lista ou crie o primeiro modelo abaixo.</p>
                ) : (
                  <div className="meta-whatsapp-template-list">
                    {templates.map((template) => (
                      <div key={`${template.id}:${template.language}`} className="meta-whatsapp-template-item">
                        <div>
                          <strong>{template.name}</strong>
                          <span>{template.language} · {template.category === 'MARKETING' ? 'Marketing' : template.category === 'AUTHENTICATION' ? 'Autenticação' : 'Utilidade'}</span>
                        </div>
                        <span className={`meta-whatsapp-template-status meta-whatsapp-template-status--${String(template.status || '').toLowerCase()}`}>
                          {templateStatusLabel(template.status)}
                        </span>
                        {template.scheduled_category && (
                          <small>
                            A Meta programou a mudança para {template.scheduled_category === 'MARKETING' ? 'Marketing' : template.scheduled_category === 'AUTHENTICATION' ? 'Autenticação' : 'Utilidade'}
                            {template.scheduled_category_at ? ` em ${new Date(template.scheduled_category_at).toLocaleString('pt-BR')}` : ''}.
                          </small>
                        )}
                        {template.recategorized_from && template.recategorized_from !== template.category && (
                          <small className="meta-whatsapp-template-recategorized">
                            Criado como {categoryLabel(template.recategorized_from)}; a Meta reclassificou como {categoryLabel(template.category)}
                            {template.recategorized_at ? ` em ${new Date(template.recategorized_at).toLocaleString('pt-BR')}` : ''}.
                            {' '}A Meta considera Marketing toda mensagem de apresentação, divulgação ou retomada de contato. Para Utilidade, o texto precisa citar a solicitação específica do cliente.
                          </small>
                        )}
                        {hasTemplateRejectionReason(template.rejected_reason) && (
                          <small>Motivo informado pela Meta: {template.rejected_reason}</small>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                <div className="meta-whatsapp-template-review-heading">
                  <div>
                    <strong>Criar modelo de mensagem</strong>
                    <span>Prepare uma mensagem para iniciar ou retomar atendimentos. Nada será criado até a confirmação final.</span>
                  </div>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => setTemplateEditors((current) => ({ ...current, [channel.id]: !current[channel.id] }))}
                  >
                    <FaPlus /> {templateEditors[channel.id] ? 'Fechar criação' : 'Novo modelo'}
                  </button>
                </div>

                {templateEditors[channel.id] && (
                  <div className="meta-whatsapp-template-review-form">
                    <p className="meta-whatsapp-template-warning">
                      Preencher este formulário não altera a conta. A criação acontece somente após clicar no botão final e confirmar. Escolha Utilidade apenas para confirmação ou atualização específica de um pedido, serviço ou ação do cliente; ofertas, divulgação e prospecção são Marketing. A Meta pode mudar a categoria, recusar ou pausar o modelo. Ele só poderá ser enviado se estiver aprovado e houver autorização válida do destinatário.
                    </p>
                    {draft.category === 'UTILITY' && (
                      <div className="meta-whatsapp-template-guide">
                        <strong>Como a Meta decide a categoria</strong>
                        <span>Utilidade precisa ser sobre algo que o cliente pediu ou fez (formulário, pedido, orçamento, agendamento), sem tom promocional. Apresentação, divulgação e retomada de contato a Meta reclassifica como Marketing automaticamente, mesmo marcando Utilidade.</span>
                        <span><b>Fica como Utilidade:</b> {UTILITY_GOOD_EXAMPLE}</span>
                        <span><b>Vira Marketing:</b> {UTILITY_BAD_EXAMPLE}</span>
                        {utilityTemplateWarnings(draft.text).map((warning) => (
                          <em key={warning}>⚠ {warning}</em>
                        ))}
                      </div>
                    )}
                    {draft.category === 'MARKETING' && !config.marketing_messages_api_enabled && (
                      <p className="meta-whatsapp-template-warning">O envio de Marketing ainda não está habilitado neste CRM. Criar e aprovar um modelo não libera seu envio automaticamente.</p>
                    )}
                    <div className="meta-whatsapp-template-grid">
                      <label>
                        Nome técnico
                        <input
                          value={draft.name}
                          onChange={(event) => updateTemplateDraft(channel.id, { name: event.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '').slice(0, 512) })}
                        />
                      </label>
                      <label>
                        Idioma
                        <select value={draft.language} onChange={(event) => updateTemplateDraft(channel.id, { language: event.target.value })}>
                          <option value="pt_BR">Português (Brasil)</option>
                          <option value="en_US">Inglês (Estados Unidos)</option>
                          <option value="es">Espanhol</option>
                        </select>
                      </label>
                      <label>
                        Categoria
                        <select value={draft.category} onChange={(event) => updateTemplateDraft(channel.id, { category: event.target.value })}>
                          <option value="UTILITY">Utilidade</option>
                          <option value="MARKETING">Marketing</option>
                        </select>
                      </label>
                    </div>
                    <label>
                      Texto do modelo
                      <textarea
                        value={draft.text}
                        maxLength={1024}
                        onChange={(event) => updateTemplateDraft(channel.id, { text: event.target.value })}
                      />
                    </label>
                    <button
                      type="button"
                      disabled={busy || !draft.name || !draft.text.trim() || Boolean(templateResult)}
                      onClick={() => void createTemplate(channel)}
                    >
                      <FaPlus /> {templateResult ? 'Modelo criado nesta sessão' : 'Criar e enviar para análise'}
                    </button>
                    {templateResult && (
                      <div className="meta-whatsapp-template-result">
                        Modelo <strong>{templateResult.name}</strong> criado. Status: <strong>{templateStatusLabel(templateResult.status)}</strong>.
                        <button type="button" onClick={() => {
                          setTemplateResults((current) => ({ ...current, [channel.id]: null }));
                          setTemplateDrafts((current) => ({ ...current, [channel.id]: { ...TEMPLATE_DEFAULT, name: '' } }));
                        }}><FaPlus /> Preparar outro modelo</button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {channel.onboarding_mode !== 'coexistence' && channel.registration_status !== 'registered' && (
                <div className="meta-whatsapp-register-row">
                  <span className="meta-whatsapp-secret-field">
                    <input
                      type={visibleRegistrationPins[channel.id] ? 'text' : 'password'}
                      value={registrationPins[channel.id] || ''}
                      onChange={(event) => setRegistrationPins({ ...registrationPins, [channel.id]: event.target.value.replace(/\D/g, '').slice(0, 6) })}
                      placeholder="PIN de 6 dígitos"
                      inputMode="numeric"
                      autoComplete="new-password"
                    />
                    <button type="button" onClick={() => setVisibleRegistrationPins((current) => ({ ...current, [channel.id]: !current[channel.id] }))} aria-label={visibleRegistrationPins[channel.id] ? 'Ocultar PIN' : 'Mostrar PIN'} title={visibleRegistrationPins[channel.id] ? 'Ocultar PIN' : 'Mostrar PIN'}>
                      {visibleRegistrationPins[channel.id] ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </span>
                  <button type="button" disabled={busy || (registrationPins[channel.id] || '').length !== 6} onClick={() => runAction(
                    async () => {
                      try {
                        await api.post(`/whatsapp-meta/channels/${channel.id}/register`, { pin: registrationPins[channel.id] });
                      } finally {
                        setRegistrationPins((current) => ({ ...current, [channel.id]: '' }));
                        setVisibleRegistrationPins((current) => ({ ...current, [channel.id]: false }));
                      }
                    },
                    'Número registrado na Cloud API.',
                  )}>Registrar número</button>
                </div>
              )}

              <button type="button" className="meta-whatsapp-danger" disabled={busy} onClick={() => {
                if (window.confirm('Desconectar este número do CRM? O atendimento por ele será interrompido nesta tenant; o histórico ficará preservado aqui. Para reconectar o mesmo número, será necessário repetir o fluxo da Meta nesta tenant. Desconectar não transfere esse número nem seu histórico para outra tenant.')) {
                  void runAction(() => api.delete(`/whatsapp-meta/channels/${channel.id}`), 'Número desconectado do CRM.');
                }
              }}><FaTrash /> Desconectar do CRM</button>
            </article>
          );})}
        </section>
    </main>
  );

  if (isNewLayout) {
    return (
      <div className="new-home meta-whatsapp-new-shell" data-theme={interfaceTheme}>
        <NewSidebar
          user={user}
          getAccessLevel={getAccessLevel}
          theme={interfaceTheme}
          onThemeChange={toggleInterfaceTheme}
          themeSaving={interfaceThemeSaving}
          onLogout={handleLogout}
          showValues={getAccessLevel('valor') && user?.home_new_show_values === true}
          activePath="/whatsapp-meta"
          mobileOpen={mobileNavigationOpen}
          onMobileClose={() => setMobileNavigationOpen(false)}
        />
        <div className="new-home-workspace meta-whatsapp-new-workspace">
          <header className="meta-whatsapp-new-topbar">
            <div className="meta-whatsapp-new-topbar-title">
              <button
                type="button"
                className="meta-whatsapp-mobile-menu"
                onClick={() => setMobileNavigationOpen(true)}
                aria-label="Abrir menu de navegação"
                title="Abrir menu"
              >
                <FaBars aria-hidden="true" />
              </button>
              <span className="meta-whatsapp-new-topbar-mark">
                <img src="/logo192.png" alt="" aria-hidden="true" />
              </span>
              <div>
                <span>Canais oficiais</span>
                <h1>Atendimento pelo WhatsApp Business</h1>
              </div>
            </div>
            <div className="meta-whatsapp-new-topbar-actions">
              <span className={config?.enabled ? 'meta-status-ok' : 'meta-status-off'}>
                {config?.enabled ? 'Integração disponível' : 'Integração indisponível'}
              </span>
              <button type="button" onClick={() => navigate('/home-new')}>Voltar ao CRM</button>
            </div>
          </header>
          <div className="meta-whatsapp-content-scroll">{content}</div>
        </div>
        {openCloseModalAvatar && <Avatar />}
        {openCloseImportExcelSuiteFlow && <ImportExcelSuiteFlow />}
        <WhatsappZapiFloatingButton theme={interfaceTheme} />
      </div>
    );
  }

  return (
    <>
      <Header />
      {content}
    </>
  );
}

export default WhatsappMetaSettings;
